import { h, mount, clear } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { toast } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { flashcardsService, BOX_WHEN, type Flashcard } from "@/services/flashcards.service";
import { markPracticed } from "@/services/streak.service";
import { recordCardReview } from "@/services/learn-stats.service";
import { addErrorCard } from "@/services/error-deck.service";
import { matchText } from "@/features/pfad/scoring";
import { cardEditor, pickRange, syncNoteCards } from "@/features/karten/editor";
import { aiJson, aiAvailableSync } from "@/services/ai.service";
import { topicSources } from "@/services/source.service";
import { pfRoot, pfHead, pfLoading, pfButton, pfBig, pfEmpty, onEnter } from "@/features/pfad/shell";
import { navigate, type RouteContext } from "@/app/router";
import type { Topic } from "@/types/models";

type Mode = "leitner" | "zuordnen" | "pruefung";
interface Setup { side: "front" | "back"; count: number; from: number; timer: boolean }

const SETUP_KEY = "lernapp.kartensetup.v1";

function readSetup(): Setup {
  try {
    const r = JSON.parse(localStorage.getItem(SETUP_KEY) ?? "{}") as Partial<Setup>;
    return {
      side: r.side === "back" ? "back" : "front",
      count: Number(r.count) > 0 ? Number(r.count) : 20,
      from: Number(r.from) > 0 ? Number(r.from) : 1,
      timer: Boolean(r.timer),
    };
  } catch {
    return { side: "front", count: 20, from: 1, timer: false };
  }
}

export async function CardsPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const mode = (ctx.params.mode ?? "") as Mode | "";
  const toLearn = () => navigate(`/topic/${topicId}/learn`);
  const toHub = () => navigate(`/topic/${topicId}/karten`);

  const head = pfHead(mode ? toHub : toLearn);
  const stage = h("div", { class: "pf-stage" });
  const foot = h("div", { class: "pf-foot" });
  const root = pfRoot(head.el, stage, foot);
  clear(foot);

  mount(stage, h("div", { class: "pf-body" }, pfLoading("Karten werden geladen")));

  let topic: Topic;
  let cards: Flashcard[];
  try {
    [topic, cards] = await Promise.all([topicsService.get(topicId), flashcardsService.listByTopic(topicId)]);
  } catch (err) {
    mount(stage, h("div", { class: "pf-body" }, pfEmpty("Karten nicht ladbar", pbErrorMessage(err), pfButton("Zurück", toLearn, "gray"))));
    return root;
  }
  cards = await syncNoteCards(topicId, cards).catch(() => cards);

  if (!mode) renderHub();
  else if (!cards.length) {
    mount(stage, h("div", { class: "pf-body" },
      pfEmpty("Noch keine Karten", "Lege zuerst welche an.", pfButton("Karten verwalten", toHub))));
  } else renderSetup(mode);
  return root;

  function renderHub(): void {
    head.set(0, 0, 1);
    // Fortlaufender Ausschnitt fürs "KI ergänzen": jeder Klick bekommt ein
    // ANDERES Materialstück zu sehen (statt immer nur den Anfang), sonst
    // findet die KI bei großem Material nach dem ersten Klick nichts Neues
    // mehr, obwohl der Rest des Textes nie gelesen wurde.
    let materialOffset = 0;
    type Tab = "ueben" | "tabelle" | "text";
    let tab: Tab = cards.length ? "ueben" : "tabelle";
    const panel = h("div");

    const modes: { key: Mode; title: string; desc: string; ic: Parameters<typeof icon>[0]; color: string }[] = [
      { key: "leitner", title: "Leitner-System", desc: "Fünf Fächer, richtig rückt vor", ic: "layers", color: "green" },
      { key: "zuordnen", title: "Zuordnen", desc: "Vier Rückseiten zur Auswahl", ic: "shuffle", color: "blue" },
      { key: "pruefung", title: "Prüfung", desc: "Lösung eintippen", ic: "pencil", color: "purple" },
    ];

    const names: [Tab, string][] = [["ueben", "Üben"], ["tabelle", "Tabelle"], ["text", "Text"]];
    const tabs = names.map(([k, label]) => h("button", { type: "button", onclick: () => show(k) }, label));

    function show(next: Tab): void {
      tab = next;
      tabs.forEach((b, i) => b.classList.toggle("on", names[i][0] === tab));
      if (tab === "ueben") {
        mount(panel, h("div", { class: "pf-modes" },
          ...modes.map((m) => h("button", {
            class: `pf-mode c-${m.color}`, type: "button",
            onclick: () => (cards.length ? navigate(`/topic/${topicId}/karten/${m.key}`) : show("tabelle")),
          },
            h("span", { class: "pf-mode-ic", html: icon(m.ic, 24) }),
            h("span", { style: "flex:1;min-width:0" },
              h("span", { class: "t", style: "display:block" }, m.title),
              h("span", { class: "d", style: "display:block" }, m.desc))))));
        mount(foot, h("div", { class: "pf-foot-in" },
          cards.length
            ? pfBig("Los geht's", () => navigate(`/topic/${topicId}/karten/leitner`))
            : pfBig("Karten anlegen", () => show("tabelle"), "blue")));
      } else {
        mount(panel, cardEditor(topicId, cards, tab === "text" ? "code" : "table", (saved) => { cards = saved; }));
        mount(foot, h("div", { class: "pf-foot-in" },
          h("div", { class: "pf-actions" }, aiBtn(), pfBig("Üben", () => show("ueben")))));
      }
    }

    function aiBtn(): HTMLElement {
      const b = h("button", { class: "pf-btn ghost", type: "button" },
        h("span", { class: "row", html: icon("sparkles", 16) }), "KI ergänzen") as HTMLButtonElement;
      b.onclick = async () => {
        if (!aiAvailableSync()) return toast("Login nötig.", "error");
        b.disabled = true;
        b.textContent = "liest Material …";
        try {
          const src = await topicSources(topic);
          if (src.words < 60) { toast("Zu wenig Material.", "error"); return; }
          const have = cards.map((c) => c.front).slice(0, 60).join(" | ");
          // Gesenkt (war 14000/1800) – zusammen mit dem System-Prompt und
          // max_tokens muss die Anfrage unter Groqs Minuten-Kontingent
          // bleiben (siehe GROQ_SAFE_PROMPT_TOKENS in ai.service.ts), sonst
          // 413 "Request too large". Damit ein Klick trotzdem irgendwann das
          // GANZE Material abdeckt statt immer nur den Anfang zu sehen,
          // wandert der Ausschnitt bei jedem Klick weiter (siehe materialOffset).
          const CHUNK = 6000;
          const excerpt = src.text.slice(materialOffset, materialOffset + CHUNK) || src.text.slice(0, CHUNK);
          materialOffset = materialOffset + CHUNK >= src.text.length ? 0 : materialOffset + CHUNK;
          const r = await aiJson<{ cards?: { front?: string; back?: string }[] }>(
            "Sammle die FACHBEGRIFFE aus dem Material und definiere sie. Kein Vorwissen, " +
            "keine Fragen, keine Aufgaben. front = der Begriff selbst (1-4 Wörter, genau wie " +
            "im Material geschrieben), back = die Definition in einem knappen Satz (max 15 Wörter). " +
            'Gib NUR kompaktes JSON in einer Zeile: {"cards":[{"front":"…","back":"…"}]} – 12 Karten, Deutsch.' +
            (have ? `\nDiese Begriffe gibt es schon, nimm ANDERE: ${have}` : ""),
            excerpt, 1200);
          const known = new Set(cards.map((c) => c.front.toLowerCase()));
          const fresh = (r.cards ?? [])
            .map((c) => ({ front: String(c.front ?? "").trim(), back: String(c.back ?? "").trim(), origin: "ki" as const }))
            .filter((c) => c.front && c.back && !known.has(c.front.toLowerCase()));
          if (!fresh.length) { toast("Nichts Neues gefunden."); return; }
          cards = await flashcardsService.replaceAll(topicId, [
            ...cards.map((c) => ({ id: c.id, front: c.front, back: c.back, origin: c.origin })),
            ...fresh,
          ]);
          show(tab);
          toast(`${fresh.length} Karten ergänzt.`, "success");
        } catch (err) {
          toast(pbErrorMessage(err), "error");
        } finally {
          b.disabled = false;
          b.textContent = "KI ergänzen";
        }
      };
      return b;
    }

    mount(stage, h("div", { class: "pf-body" },
      h("div", { class: "pf-setup" },
        h("h1", null, "Karteikarten"),
        h("p", { class: "lead" }, `${topic.title} · ${cards.length} Karten · ${flashcardsService.due(cards).length} fällig`),
        h("div", { class: "pf-tabbar" }, ...tabs),
        panel)));
    show(tab);
  }

  function renderSetup(m: Mode): void {
    head.set(0, 0, 1);
    const setup = readSetup();
    setup.count = Math.min(setup.count, cards.length);
    setup.from = Math.min(setup.from, cards.length);

    const sides: ["front" | "back", string][] = [["front", "Vorderseite zeigen"], ["back", "Rückseite zeigen"]];
    const sideBtns = sides.map(([v, label]) =>
      h("button", { type: "button", class: setup.side === v ? "on" : "", onclick: () => {
        setup.side = v;
        sideBtns.forEach((b, i) => b.classList.toggle("on", sides[i][0] === v));
      } }, label));

    const countOut = h("output", { class: "pf-rangeval" }, String(setup.count));
    const count = h("input", { class: "pf-range", type: "range", min: 1, max: cards.length, value: String(setup.count) }) as HTMLInputElement;
    count.oninput = () => (countOut.textContent = count.value);

    const from = h("select", { class: "pf-select" },
      ...cards.map((c, i) => h("option", { value: String(i + 1) }, `${i + 1}. ${c.front.slice(0, 40)}`))) as HTMLSelectElement;
    from.value = String(setup.from);

    const timerBox = h("input", { type: "checkbox", checked: setup.timer }) as HTMLInputElement;

    mount(stage, h("div", { class: "pf-body" },
      h("div", { class: "pf-setup" },
        h("h1", null, m === "leitner" ? "Leitner-System" : m === "zuordnen" ? "Zuordnen" : "Prüfung"),
        h("p", { class: "lead" }, `${cards.length} Karten in „${topic.title}"`),
        h("div", { class: "pf-field" }, h("label", null, "Was wird abgefragt?"), h("div", { class: "pf-seg" }, ...sideBtns)),
        h("div", { class: "pf-field" },
          h("label", null, "Wie viele Karten?"),
          h("div", { class: "pf-rangerow" }, count, countOut, h("span", { class: "pf-rangemax" }, `/ ${cards.length}`))),
        h("div", { class: "pf-field" }, h("label", null, "Ab welcher Karte?"), from),
        h("div", { class: "pf-field" },
          h("label", { class: "pf-switch" }, timerBox, h("span", null, "Timer oben rechts mitlaufen lassen"))))));

    const go = () => {
      setup.count = Math.max(1, Math.min(Number(count.value) || 1, cards.length));
      setup.from = Math.max(1, Math.min(Number(from.value) || 1, cards.length));
      setup.timer = timerBox.checked;
      try { localStorage.setItem(SETUP_KEY, JSON.stringify(setup)); } catch { /* ignore */ }
      start(m, setup);
    };
    onEnter(root, go);
    mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Los geht's", go)));
  }

  function start(m: Mode, setup: Setup): void {
    const deck = pickRange(cards, setup.from, setup.count);
    if (!deck.length) return toast("Keine Karten in dieser Auswahl.");
    onEnter(root, () => {});

    clear(head.right);
    let seconds = 0;
    let tick = 0;
    if (setup.timer) {
      const t = h("span", { class: "pf-timer" }, "00:00");
      head.right.appendChild(t);
      tick = window.setInterval(() => {
        if (!document.body.contains(t)) return window.clearInterval(tick);
        seconds++;
        t.textContent = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
      }, 1000);
    } else {
      head.right.appendChild(h("span", { class: "pf-streak", html: icon("zap", 20) }));
    }

    const ask = (c: Flashcard) => (setup.side === "front" ? c.front : c.back);
    const solve = (c: Flashcard) => (setup.side === "front" ? c.back : c.front);
    const miss = (c: Flashcard) => addErrorCard(topicId, ask(c), solve(c));

    let done = 0;
    let wrong = 0;

    const finish = () => {
      window.clearInterval(tick);
      markPracticed();
      void recordCardReview(topicId, done);
      const pct = done ? Math.round(((done - wrong) / done) * 100) : 0;
      mount(stage, h("div", { class: "pf-body" },
        h("div", { class: "pf-result" },
          h("div", { class: `pf-crown ${pct >= 80 ? "gold" : pct >= 50 ? "green" : "blue"}`, html: icon("award", 44) }),
          h("h1", null, "Durchgang geschafft!"),
          h("div", { class: "pf-cards" },
            rcard("green", "Richtig", String(done - wrong)),
            rcard("orange", "Fehler", String(wrong)),
            rcard("blue", "Karten", String(done)),
            rcard("gold", "Zeit", `${Math.max(1, Math.round(seconds / 60))} min`)))));
      foot.className = "pf-foot";
      mount(foot, h("div", { class: "pf-foot-in" },
        h("div", { class: "pf-actions" },
          pfButton("Andere Übung", toHub, "ghost"),
          pfBig("Nochmal", () => start(m, setup)))));
      onEnter(root, () => start(m, setup));
    };

    if (m === "leitner") leitner();
    else if (m === "zuordnen") zuordnen();
    else pruefung();

    function leitner(): void {
      let i = 0;
      const show = () => {
        if (i >= deck.length) return finish();
        head.set(done, wrong, deck.length);
        const card = deck[i];
        let flipped = false;

        const scene = h("div", { class: "pf-card-scene" },
          h("div", { class: "pf-card-inner" },
            h("div", { class: "pf-face" },
              h("div", { class: "lbl" }, `Karte ${i + 1}/${deck.length} · Fach ${card.box} (${BOX_WHEN[card.box]})`),
              h("div", { class: "txt" }, ask(card)),
              h("div", { class: "tap" }, "Tippen oder Enter zum Umdrehen")),
            h("div", { class: "pf-face back" },
              h("div", { class: "lbl" }, "Lösung"),
              h("div", { class: "txt" }, solve(card)))));

        const grade = (ok: boolean) => {
          void flashcardsService.grade(card, ok).catch(() => {});
          done++;
          if (!ok) { wrong++; miss(card); deck.push(card); }
          i++;
          show();
        };

        const flip = () => {
          if (flipped) return;
          flipped = true;
          scene.classList.add("flipped");
          foot.className = "pf-foot";
          mount(foot, h("div", { class: "pf-foot-in" },
            h("p", { class: "pf-gradehint" }, `Gewusst → Fach ${Math.min(5, card.box + 1)} · Nicht gewusst → Fach 1`),
            h("div", { class: "pf-actions" },
              h("button", { class: "pf-btn red block", type: "button", onclick: () => grade(false) }, "Nicht gewusst"),
              h("button", { class: "pf-btn block", type: "button", onclick: () => grade(true) }, "Gewusst"))));
          onEnter(root, () => grade(true));
        };

        scene.addEventListener("click", flip);
        mount(stage, h("div", { class: "pf-body" }, scene));
        foot.className = "pf-foot";
        mount(foot, h("div", { class: "pf-foot-in" }, pfBig("Umdrehen", flip, "blue")));
        onEnter(root, flip);
      };
      show();
    }

    function zuordnen(): void {
      const groups: Flashcard[][] = [];
      for (let i = 0; i < deck.length; i += 4) groups.push(deck.slice(i, i + 4));
      let g = 0;

      const showGroup = () => {
        if (g >= groups.length) return finish();
        const group = groups[g];
        const remaining = [...group];
        const tiles = new Map<string, HTMLButtonElement>();
        const promptEl = h("div", { class: "pf-zu-prompt" });
        const grid = h("div", { class: "pf-zu-grid" });

        for (const c of shuffle([...group])) {
          const btn = h("button", { class: "pf-tile", type: "button", onclick: () => pick(c) }, solve(c)) as HTMLButtonElement;
          tiles.set(c.id, btn);
          grid.appendChild(btn);
        }

        let current = remaining[0];
        promptEl.textContent = ask(current);

        function pick(chosen: Flashcard): void {
          const btn = tiles.get(chosen.id)!;
          if (chosen.id === current.id) {
            done++;
            btn.classList.add("gone");
            void flashcardsService.grade(chosen, true).catch(() => {});
            remaining.shift();
            head.set(done, wrong, deck.length);
            if (!remaining.length) { g++; window.setTimeout(showGroup, 320); return; }
            current = remaining[0];
            promptEl.textContent = ask(current);
          } else {
            wrong++;
            miss(current);
            void flashcardsService.grade(current, false).catch(() => {});
            head.set(done, wrong, deck.length);
            btn.classList.add("bad");
            window.setTimeout(() => btn.classList.remove("bad"), 420);
          }
        }

        head.set(done, wrong, deck.length);
        mount(stage, h("div", { class: "pf-body" },
          h("p", { class: "muted", style: "text-align:center;margin:0 0 4px;font-size:13px" }, `Runde ${g + 1} von ${groups.length}`),
          promptEl, grid));
        clear(foot);
        foot.className = "pf-foot";
      };
      showGroup();
    }

    function pruefung(): void {
      let i = 0;
      const show = () => {
        if (i >= deck.length) return finish();
        head.set(done, wrong, deck.length);
        const card = deck[i];
        let checked = false;

        const input = h("input", {
          class: "pf-input", type: "text", autocomplete: "off", autocapitalize: "off",
          spellcheck: false, placeholder: "Lösung eintippen …",
        }) as HTMLInputElement;

        const action = h("button", { class: "pf-btn block", type: "button", disabled: true }, "Prüfen") as HTMLButtonElement;
        const fbSlot = h("div");

        input.addEventListener("input", () => { if (!checked) action.disabled = !input.value.trim(); });

        action.onclick = () => {
          if (checked) { i++; show(); return; }
          if (!input.value.trim()) return;
          checked = true;
          input.disabled = true;
          const res = matchText(input.value, solve(card));
          const ok = res === "exact";
          done++;
          if (!ok) { wrong++; miss(card); deck.push(card); }
          input.classList.add(ok ? "good" : res === "typo" ? "soft" : "bad");
          foot.className = "pf-foot " + (ok ? "correct" : res === "typo" ? "tip" : "wrong");
          void flashcardsService.grade(card, ok).catch(() => {});
          mount(fbSlot, h("div", { class: "pf-fb" },
            h("div", { class: "pf-fb-badge" }, ok ? "✓" : res === "typo" ? "~" : "✗"),
            h("div", { class: "pf-fb-main" },
              h("h4", null, ok ? "Richtig!" : res === "typo" ? "Fast – Tippfehler" : "Nicht ganz"),
              h("p", null, `Lösung: ${solve(card)}`))));
          head.set(done, wrong, deck.length);
          action.textContent = i + 1 >= deck.length ? "Ergebnis" : "Weiter";
          action.disabled = false;
        };

        mount(stage, h("div", { class: "pf-body" },
          h("p", { class: "muted", style: "margin:0 0 6px;font-size:13px" }, `Karte ${i + 1} von ${deck.length}`),
          h("h2", { class: "pf-q-title" }, ask(card)),
          input,
          card.hint ? h("p", { class: "pf-q-hint", style: "margin-top:12px" }, `Tipp: ${card.hint}`) : null));
        foot.className = "pf-foot";
        mount(foot, h("div", { class: "pf-foot-in" }, fbSlot, action));
        onEnter(root, () => { if (!action.disabled) action.click(); });
        input.focus();
      };
      show();
    }
  }

  function rcard(color: string, label: string, value: string): HTMLElement {
    return h("div", { class: `pf-rcard c-${color}` },
      h("div", { class: "in" }, h("div", { class: "lab" }, label), h("div", { class: "val" }, value)));
  }
}

function shuffle<T>(list: T[]): T[] {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}
